import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import {
  ArrowLeft, Edit2, Plus, FileText, Briefcase,
  Phone, Mail, MapPin, Building, Tag, Calendar
} from 'lucide-react'
import { format } from 'date-fns'

const CLIENT_TYPES = ['individual', 'corporate', 'non-profit', 'government', 'other']
const SOURCES = ['google', 'facebook', 'referral', 'returning', 'other', 'lead-conversion']

function fmtDate(value, withTime = false) {
  if (!value) return '—'
  try {
    return format(new Date(value), withTime ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy')
  } catch {
    return '—'
  }
}

function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || '',
    email: client.email || '',
    phone: client.phone || '',
    company: client.company || '',
    address: client.address || '',
    client_type: client.client_type || 'individual',
    source: client.source || 'google',
    notes: client.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function set(field, val) { setForm(f => ({ ...f, [field]: val })) }

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    const { data, error } = await supabase.from('clients').update(form).eq('id', client.id).select().single()
    if (error) { setError(error.message); setSaving(false) }
    else onSaved(data)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700 }}>Edit Client</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Company</label>
                <input className="form-input" value={form.company} onChange={e => set('company', e.target.value)} />
              </div>
            </div>
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input className="form-input" value={form.address} onChange={e => set('address', e.target.value)} />
            </div>
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Client Type</label>
                <select className="form-select" value={form.client_type} onChange={e => set('client_type', e.target.value)}>
                  {CLIENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Source</label>
                <select className="form-select" value={form.source} onChange={e => set('source', e.target.value)}>
                  {SOURCES.map(s => <option key={s} value={s}>{s.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Internal Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function ClientDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [client, setClient] = useState(null)
  const [quotes, setQuotes] = useState([])
  const [jobs, setJobs] = useState([])
  const [lead, setLead] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showEdit, setShowEdit] = useState(false)

  useEffect(() => { loadClient() }, [id])

  async function loadClient() {
    setLoading(true)
    setError('')

    try {
      const [clientRes, quotesRes, jobsRes] = await Promise.all([
        supabase.from('clients').select('*').eq('id', id).single(),
        supabase.from('quotes').select('id, quote_number, event_date, event_type, status, grand_total, payment_status, created_at').eq('client_id', id).order('created_at', { ascending: false }),
        supabase.from('jobs').select('id, event_date, event_type, status, payment_status, balance_due, admin_notes, created_at').eq('client_id', id).order('event_date', { ascending: false }),
      ])

      if (clientRes.error) { navigate('/clients'); return }
      if (quotesRes.error) throw quotesRes.error
      if (jobsRes.error) throw jobsRes.error

      setClient(clientRes.data)
      setQuotes(quotesRes.data || [])
      setJobs(jobsRes.data || [])

      if (clientRes.data) {
        const { data: leadData } = await supabase
          .from('leads')
          .select('id, name, status, created_at')
          .eq('converted_client_id', id)
          .limit(1)
          .single()
        if (leadData) setLead(leadData)
      }
    } catch (err) {
      console.error('Failed to load client detail:', err)
      setError(err?.message || 'Failed to load client details.')
    } finally {
      setLoading(false)
    }
  }

  const activeJobsCount = useMemo(() => jobs.filter(j => !['completed', 'cancelled'].includes(j.status)).length, [jobs])
  const latestQuote = quotes[0] || null
  const latestJob = jobs[0] || null
  const latestQuoteDate = latestQuote?.created_at || null
  const latestJobDate = latestJob?.created_at || null
  const latestActivityDate = [client?.created_at, latestQuoteDate, latestJobDate]
    .filter(Boolean)
    .map(d => new Date(d))
    .sort((a, b) => b - a)[0] || null

  const timelineItems = useMemo(() => {
    const items = []

    if (client?.created_at) {
      items.push({
        key: `client-created-${client.id}`,
        label: 'Client created',
        date: client.created_at,
        type: 'client',
      })
    }

    for (const q of quotes) {
      if (q?.created_at) {
        items.push({
          key: `quote-created-${q.id}`,
          label: `Quote created${q.quote_number ? ` (${q.quote_number})` : ''}`,
          date: q.created_at,
          type: 'quote',
          id: q.id,
          path: `/quotes/${q.id}`,
        })
      }

      if (q?.status === 'converted') {
        items.push({
          key: `quote-converted-${q.id}`,
          label: `Quote converted to job${q.quote_number ? ` (${q.quote_number})` : ''}`,
          date: q.created_at,
          type: 'quote',
          id: q.id,
          path: `/quotes/${q.id}`,
        })
      }
    }

    for (const j of jobs) {
      if (j?.created_at) {
        items.push({
          key: `job-created-${j.id}`,
          label: 'Job created',
          date: j.created_at,
          type: 'job',
          id: j.id,
          path: `/jobs/${j.id}`,
        })
      }

      if (false) {
        items.push({
          key: `job-updated-${j.id}`,
          label: 'Job updated',
          date: j.created_at,
          type: 'job',
          id: j.id,
          path: `/jobs/${j.id}`,
        })
      }
    }

    return items
      .filter(i => i?.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 12)
  }, [client?.id, client?.created_at, quotes, jobs])

  if (loading) return <div className="loading-state">Loading client…</div>
  if (!client) {
    if (error) return <div className="alert alert-error" style={{ margin: 16 }}>{error}</div>
    return null
  }

  return (
    <div data-testid="client-workspace-page">
      {error && <div className="alert alert-error" data-testid="feedback-banner-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="page-header">
        <div className="flex items-center gap-3">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/clients')}>
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <h1 className="page-title">Client Workspace</h1>
            <p className="page-subtitle">{client.name}{client.company ? ` · ${client.company}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="client-header-actions">
          <Link to={`/quotes/new?client=${client.id}`} data-testid="client-create-quote-button-header" className="btn btn-secondary btn-sm">
            <FileText size={14} /> Create Quote
          </Link>
          {latestQuote && (
            <Link to={`/quotes/${latestQuote.id}`} className="btn btn-ghost btn-sm">
              View Latest Quote
            </Link>
          )}
          {latestJob && (
            <Link to={`/jobs/${latestJob.id}`} className="btn btn-ghost btn-sm">
              View Latest Job
            </Link>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => setShowEdit(true)}>
            <Edit2 size={14} /> Edit
          </button>
        </div>
      </div>

      <div className="card" data-testid="client-summary-section" style={{ marginBottom: 'var(--sp-4)' }}>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {client.email && (
              <div className="detail-row"><Mail size={15} className="detail-icon" /><a href={`mailto:${client.email}`}>{client.email}</a></div>
            )}
            {client.phone && (
              <div className="detail-row"><Phone size={15} className="detail-icon" /><a href={`tel:${client.phone}`}>{client.phone}</a></div>
            )}
            {client.address && (
              <div className="detail-row"><MapPin size={15} className="detail-icon" /><span>{client.address}</span></div>
            )}
          </div>
          <div className="stat-card" style={{ padding: 12 }}>
            <div className="stat-label">Total Quotes</div>
            <div className="stat-value" style={{ fontSize: 'var(--text-xl)' }}>{quotes.length}</div>
          </div>
          <div className="stat-card" style={{ padding: 12 }}>
            <div className="stat-label">Active Jobs</div>
            <div className="stat-value" style={{ fontSize: 'var(--text-xl)' }}>{activeJobsCount}</div>
          </div>
          <div className="stat-card" style={{ padding: 12 }}>
            <div className="stat-label">Latest Activity</div>
            <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>{latestActivityDate ? fmtDate(latestActivityDate) : '—'}</div>
          </div>
        </div>
      </div>

      <div className="workspace-layout">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div className="card" data-testid="client-quotes-section">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <FileText size={16} />
                <h3 style={{ fontWeight: 600 }}>Quotes</h3>
                <span className="badge badge-draft">{quotes.length}</span>
              </div>
              <Link to={`/quotes/new?client=${client.id}`} className="btn btn-secondary btn-sm">
                <Plus size={14} /> Create Quote
              </Link>
            </div>
            {quotes.length === 0 ? (
              <div className="empty-state" data-testid="empty-state-client-quotes" style={{ padding: 24 }}>
                <p style={{ fontWeight: 600 }}>No quotes for this client yet</p>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Start by creating a quote from this workspace.</p>
                <div style={{ marginTop: 10 }}>
                  <Link to={`/quotes/new?client=${client.id}`} className="btn btn-secondary btn-sm">Create Quote</Link>
                </div>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Quote #</th>
                    <th>Event Date</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map(q => (
                    <tr key={q.id} data-testid={`client-quote-row-${q.id}`}>
                      <td><span className="text-mono" style={{ fontSize: 'var(--text-xs)', fontWeight: 600 }}>{q.quote_number || '—'}</span></td>
                      <td style={{ fontSize: 'var(--text-sm)' }}>{q.event_date ? fmtDate(`${q.event_date}T00:00:00`) : '—'}</td>
                      <td><span className={`badge badge-${q.status}`}>{q.status}</span></td>
                      <td style={{ fontWeight: 600 }}>{q.grand_total != null ? `$${Number(q.grand_total).toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : '—'}</td>
                      <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{fmtDate(q.created_at)}</td>
                      <td><Link to={`/quotes/${q.id}`} className="btn btn-ghost btn-sm">View</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card" data-testid="client-jobs-section">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <Briefcase size={16} />
                <h3 style={{ fontWeight: 600 }}>Jobs</h3>
                <span className="badge badge-confirmed">{jobs.length}</span>
              </div>
              <Link to={`/jobs/new?client=${client.id}`} className="btn btn-secondary btn-sm">
                <Plus size={14} /> Create Job
              </Link>
            </div>
            {jobs.length === 0 ? (
              <div className="empty-state" data-testid="empty-state-client-jobs" style={{ padding: 24 }}>
                <p style={{ fontWeight: 600 }}>No jobs for this client yet</p>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Convert an accepted quote or create a job when work is confirmed.</p>
                <div style={{ marginTop: 10 }}>
                  <Link to={`/jobs/new?client=${client.id}`} className="btn btn-secondary btn-sm">Create Job</Link>
                </div>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Event Date</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th>Balance Due</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(j => (
                    <tr key={j.id} data-testid={`client-job-row-${j.id}`}>
                      <td style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{j.event_date ? fmtDate(`${j.event_date}T00:00:00`) : '—'}</td>
                      <td><span className={`badge badge-${j.status}`}>{j.status?.replace('_', ' ')}</span></td>
                      <td><span className={`badge badge-${j.payment_status}`}>{j.payment_status?.replace('_', ' ')}</span></td>
                      <td style={{ fontWeight: 600 }}>{j.balance_due != null ? `$${Number(j.balance_due).toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : '—'}</td>
                      <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{fmtDate(j.created_at)}</td>
                      <td><Link to={`/jobs/${j.id}`} className="btn btn-ghost btn-sm">View</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div className="card" data-testid="client-activity-section">
            <div className="card-header" style={{ padding: '14px 20px' }}>
              <h3 style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Client Activity Timeline</h3>
            </div>
            <div className="card-body" style={{ padding: '14px 20px' }}>
              {timelineItems.length === 0 ? (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>No activity available yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {timelineItems.map((item, idx) => (
                    <div
                      key={item.key}
                      data-testid={`client-activity-item-${idx}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '10px 1fr',
                        gap: 10,
                        alignItems: 'start',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 3 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-navy)', display: 'inline-block' }} />
                      </div>
                      <div style={{ borderLeft: '1px solid var(--color-border)', paddingLeft: 12, marginLeft: 2 }}>
                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{item.label}</div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                          {fmtDate(item.date, true)}
                        </div>
                        {item.path && item.type && item.id && (
                          <div style={{ marginTop: 6 }}>
                            <Link
                              to={item.path}
                              data-testid={`client-activity-link-${item.type}-${item.id}`}
                              className="btn btn-ghost btn-sm"
                              style={{ paddingLeft: 0, paddingRight: 0 }}
                            >
                              View {item.type}
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card" data-testid="client-quick-actions">
            <div className="card-header" style={{ padding: '14px 20px' }}>
              <h3 style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Quick Actions</h3>
            </div>
            <div className="card-body" style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Link to={`/quotes/new?client=${client.id}`} className="btn btn-secondary btn-sm" data-testid="client-create-quote-button">
                <FileText size={14} /> Create Quote
              </Link>
              {latestQuote && (
                <Link to={`/quotes/${latestQuote.id}`} className="btn btn-ghost btn-sm">View Latest Quote</Link>
              )}
              {latestJob && (
                <Link to={`/jobs/${latestJob.id}`} className="btn btn-ghost btn-sm">View Latest Job</Link>
              )}
              {lead && (
                <Link to={`/leads/${lead.id}`} className="btn btn-ghost btn-sm">View Original Lead</Link>
              )}
              <button className="btn btn-primary btn-sm" onClick={() => setShowEdit(true)}>
                <Edit2 size={14} /> Edit Client
              </button>
            </div>
          </div>

          {(client.client_type || client.source || client.notes) && (
            <div className="card">
              <div className="card-header" style={{ padding: '14px 20px' }}>
                <h3 style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Client Context</h3>
              </div>
              <div className="card-body" style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {client.client_type && (
                  <div className="detail-row"><Building size={15} className="detail-icon" /><span style={{ textTransform: 'capitalize' }}>{client.client_type}</span></div>
                )}
                {client.source && (
                  <div className="detail-row"><Tag size={15} className="detail-icon" /><span style={{ textTransform: 'capitalize' }}>{client.source.replace('-', ' ')}</span></div>
                )}
                {client.notes && (
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', marginTop: 4 }}>
                    {client.notes}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showEdit && (
        <EditClientModal
          client={client}
          onClose={() => setShowEdit(false)}
          onSaved={saved => { setClient(saved); setShowEdit(false) }}
        />
      )}

      <style>{`
        .workspace-layout {
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: var(--sp-5);
          align-items: start;
        }
        .detail-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: var(--text-sm);
          color: var(--color-text);
        }
        .detail-icon { color: var(--color-text-muted); margin-top: 2px; flex-shrink: 0; }
        .loading-state { padding: 60px; text-align: center; color: var(--color-text-muted); }
        @media (max-width: 1020px) {
          .workspace-layout { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  )
}
